import { useEffect, useState } from 'react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';
import { Check, Crown, Building2, TrendingUp, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Plan, ModuleMetier } from '../hooks/usePackAccess';

interface TarifsPageProps {
  onNavigate: (path: string) => void;
  onSignIn: () => void;
  onSignUp: () => void;
}

export function TarifsPage({ onNavigate, onSignIn, onSignUp }: TarifsPageProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [modules, setModules] = useState<ModuleMetier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlansAndModules();
  }, []);

  const loadPlansAndModules = async () => {
    try {
      const { data: plansData, error: plansError } = await supabase
        .from('plans')
        .select('*')
        .eq('active', true)
        .order('display_order');

      const { data: modulesData, error: modulesError } = await supabase
        .from('modules_metiers')
        .select('*');

      if (plansError) {
        console.error('Error loading plans:', plansError);
        throw plansError;
      }
      if (modulesError) {
        console.error('Error loading modules:', modulesError);
        throw modulesError;
      }

      if (plansData) setPlans(plansData);
      if (modulesData) setModules(modulesData);
    } catch (error) {
      console.error('Error loading plans and modules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Y a-t-il un engagement ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Non, aucun engagement. Vous pouvez annuler votre abonnement Trezoone à tout moment."
          }
        },
        {
          "@type": "Question",
          "name": "Puis-je changer d'offre ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, vous pouvez changer d'offre à tout moment. Si vous passez à une offre supérieure, le changement est immédiat."
          }
        },
        {
          "@type": "Question",
          "name": "Comment fonctionne le paiement Stripe ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Les paiements Trezoone sont sécurisés par Stripe, leader mondial du paiement en ligne. Vos informations bancaires ne sont jamais stockées sur nos serveurs."
          }
        },
        {
          "@type": "Question",
          "name": "Puis-je annuler à tout moment ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace compte."
          }
        }
      ]
    };

  const getPlanIcon = (slug: string) => {
    switch (slug) {
      case 'essentiel':
        return TrendingUp;
      case 'metier':
        return Building2;
      case 'pilotage':
        return Shield;
      case 'daf':
        return Crown;
      default:
        return TrendingUp;
    }
  };

  const getPlanColor = (slug: string) => {
    switch (slug) {
      case 'essentiel':
        return 'from-gray-600 to-gray-700';
      case 'metier':
        return 'from-blue-600 to-blue-700';
      case 'pilotage':
        return 'from-emerald-600 to-emerald-700';
      case 'daf':
        return 'from-amber-600 to-amber-700';
      default:
        return 'from-gray-600 to-gray-700';
    }
  };

  const formatPrice = (price: number) => {
    return (price / 100).toFixed(0);
  };

  if (loading) {
    return (
      <>
        <SeoHead
          title="Tarifs TrezoOne – Solution de gestion financière modulaire"
          description="Découvrez les packs TrezoOne : du Pack Essentiel au Pack DAF, avec modules métiers personnalisables. Sans engagement."
          canonical="/tarifs"
          keywords="tarifs gestion trésorerie, prix pilotage financier, abonnement logiciel financier, pack gestion financière"
          jsonLd={jsonLd}
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-600">Chargement...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHead
        title="Tarifs TrezoOne – Solution de gestion financière modulaire"
        description="Découvrez les packs TrezoOne : du Pack Essentiel au Pack DAF, avec modules métiers personnalisables. Sans engagement."
        canonical="/tarifs"
        keywords="tarifs gestion trésorerie, prix pilotage financier, abonnement logiciel financier, pack gestion financière"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-gray-50">
        <MarketingHeader onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />

      <main className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Tarifs TrezoOne
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Choisissez le niveau de lucidité financière dont vous avez besoin
          </p>
          <p className="text-gray-500">
            CORE universel • Modules métiers activables • Montée en gamme naturelle
          </p>
        </div>

        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl shadow-xl p-6 mb-12 text-center">
          <p className="text-xl md:text-2xl font-bold mb-2">
            Aucune connexion bancaire obligatoire • Zéro revente de données • Respect total de votre autonomie
          </p>
          <p className="text-sm md:text-base opacity-90">
            Importez simplement vos relevés Excel ou PDF. Vos données vous appartiennent.
          </p>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6 mb-12">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Le CORE : accessible à tous les packs
          </h3>
          <p className="text-gray-700 mb-4">
            Toutes nos offres incluent les fonctionnalités essentielles de gestion de trésorerie :
          </p>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Tableau de bord et vision temps réel</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Plan de trésorerie prévisionnel</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Catégorisation automatique</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Scénarios et Stress Test</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Budget prévisionnel et suivi</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Fonds de Roulement Dynamique</span>
            </div>
          </div>
        </div>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Nos Packs
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {plans.map((plan, index) => {
              const Icon = getPlanIcon(plan.slug);
              const isRecommended = plan.slug === 'metier';
              const isDaf = plan.slug === 'daf';
              const isEssentiel = plan.slug === 'essentiel';

              return (
                <div
                  key={plan.id}
                  className={`rounded-lg shadow-lg p-6 relative transition-transform hover:scale-105 ${
                    isDaf
                      ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300'
                      : isEssentiel
                      ? 'bg-white border-2 border-gray-300'
                      : 'bg-white border-2 border-gray-200'
                  } ${isRecommended ? 'transform md:scale-105 border-blue-400' : ''}`}
                >
                  {isRecommended && (
                    <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg font-semibold text-xs">
                      Recommandé
                    </div>
                  )}

                  {isDaf && (
                    <div className="absolute top-0 right-0 bg-amber-600 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg font-semibold text-xs">
                      Places limitées
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div className="flex justify-center mb-3">
                      <div className={`p-3 rounded-full bg-gradient-to-br ${getPlanColor(plan.slug)}`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>

                    <p className="text-xs text-gray-600 mb-3 min-h-[2.5rem]">
                      {plan.slug === 'essentiel' && 'CORE complet pour maîtriser votre trésorerie'}
                      {plan.slug === 'metier' && 'CORE + outils adaptés à votre secteur'}
                      {plan.slug === 'pilotage' && 'Métier + rapports mensuels automatisés'}
                      {plan.slug === 'daf' && 'Pilotage + accompagnement personnalisé'}
                    </p>

                    <div className="flex items-baseline justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{formatPrice(plan.price)}€</span>
                      <span className="text-gray-600 ml-1">/mois</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={onSignUp}
                    className={`w-full px-4 py-2.5 rounded-lg transition-colors font-semibold text-sm ${
                      isDaf
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    Choisir {plan.name}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Modules Métiers Activables
            </h3>
            <p className="text-center text-gray-600 mb-6">
              À partir du Pack Métier, activez 1 module adapté à votre secteur
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {modules.map((module) => {
                const isInDevelopment = module.slug === 'agri' || module.slug === 'pme';

                return (
                  <div key={module.id} className="bg-white rounded-lg p-6 shadow-sm relative">
                    {isInDevelopment && (
                      <div className="absolute top-0 right-0 bg-orange-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg font-semibold text-xs">
                        En développement
                      </div>
                    )}

                    <div className="flex items-center mb-3">
                      <Building2 className="h-6 w-6 text-blue-600 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        {module.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      {module.description}
                    </p>
                    <ul className="space-y-1">
                      {module.features.map((feature: string, idx: number) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-100 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Services à la demande
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Session d'analyse personnalisée
                </h4>
                <p className="text-gray-600 mb-4">
                  1h d'accompagnement individuel pour décrypter vos rapports et vos indicateurs
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  150€ <span className="text-base font-normal text-gray-600">/session</span>
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Rapport financier pour AG
                </h4>
                <p className="text-gray-600 mb-4">
                  Rapport annuel complet adapté pour assemblée générale
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  300€ <span className="text-base font-normal text-gray-600">/rapport</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Paiement et engagement
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Paiement sécurisé via Stripe
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Les paiements Trezoone sont sécurisés par Stripe, leader mondial du paiement en ligne.
                Vos informations bancaires ne sont jamais stockées sur nos serveurs.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Sans engagement, annulation libre
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Aucun engagement de durée. Vous pouvez annuler votre abonnement à tout moment.
                L'annulation prend effet à la fin de la période en cours.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Questions fréquentes
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'Y a-t-il un engagement ?',
                a: 'Non, aucun engagement. Vous pouvez annuler votre abonnement Trezoone à tout moment.'
              },
              {
                q: 'Puis-je changer d\'offre ?',
                a: 'Oui, vous pouvez changer d\'offre à tout moment. Si vous passez à une offre supérieure, le changement est immédiat.'
              },
              {
                q: 'Comment fonctionne le paiement Stripe ?',
                a: 'Les paiements Trezoone sont sécurisés par Stripe. Vos informations bancaires ne sont jamais stockées sur nos serveurs.'
              },
              {
                q: 'Puis-je annuler à tout moment ?',
                a: 'Oui, vous pouvez annuler depuis votre espace compte. L\'annulation prend effet à la fin de la période en cours.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {item.q}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            En bref : glossaire des tarifs
          </h2>

          <dl className="space-y-4">
            <div>
              <dt className="font-semibold text-gray-900">Abonnement mensuel</dt>
              <dd className="text-gray-600 mt-1">Paiement récurrent chaque mois pour accéder aux fonctionnalités Trezoone.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Sans engagement</dt>
              <dd className="text-gray-600 mt-1">Pas de durée minimale contractuelle, annulation possible à tout moment.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Stripe</dt>
              <dd className="text-gray-600 mt-1">Plateforme de paiement sécurisée.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Export PDF</dt>
              <dd className="text-gray-600 mt-1">Téléchargement de vos rapports au format PDF professionnel.</dd>
            </div>
          </dl>
        </section>

        <div className="bg-blue-600 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Prêt à voir clair dans votre trésorerie ?
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => onNavigate('/fonctionnalites')}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
            >
              Découvrir les fonctionnalités
            </button>
            <button
              onClick={onSignUp}
              className="bg-blue-700 text-white px-8 py-3 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
            >
              S'inscrire maintenant
            </button>
          </div>
        </div>
      </main>

        <MarketingFooter />
      </div>
    </>
  );
}
